import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Translation resources
const resources = {
  en: {
    translation: {
      // Landing page
      landing: {
        hero: {
          badge: 'AI WORKFORCE OPERATIONS PLATFORM',
          title: 'Manage your workforce.',
          titleHighlight: 'Operate with AI.',
          subtitle: 'Connect ChatGPT or Claude directly to ReStaff. Securely access schedules, staff, analytics, labor insights, and workforce actions using natural language — alongside the full scheduling, payroll, and timesheet suite you already get.',
          cta: 'Start Free Trial',
          demo: 'Try Live Demo',
          explore: 'Explore AI Features',
          setup: 'MCP Setup Guide'
        },
        pricing: {
          title: 'Simple, Transparent Pricing',
          subtitle: 'No hidden fees. Cancel anytime.',
          monthly: 'Monthly',
          yearly: 'Yearly',
          save: 'Save 50%+',
          trial: '30-day free trial',
          cancel: 'Cancel anytime',
          includes: 'Includes AI Workforce Assistant'
        },
        nav: {
          features: 'Features',
          pricing: 'Pricing',
          help: 'Help',
          login: 'Log In',
          quickClock: 'Quick Clock'
        }
      },
      
      // Navigation
      nav: {
        dashboard: 'Dashboard',
        staff: 'Staff',
        timeClock: 'Time Clock',
        schedule: 'Schedule',
        shiftSwaps: 'Shift Swaps',
        tasks: 'Tasks',
        announcements: 'Announcements',
        notifications: 'Notifications',
        documents: 'Documents',
        insights: 'Executive Insights',
        timesheets: 'Timesheets',
        helpCenter: 'Help Center',
        logout: 'Logout',
        settings: 'Settings'
      },
      
      // Dashboard
      dashboard: {
        welcome: 'Welcome back, {{name}}!',
        overview: 'Overview',
        labor: 'Labor',
        requests: 'Requests',
        currentStatus: 'Current Status',
        onShift: 'On Shift',
        offShift: 'Off Shift',
        clockIn: 'Clock In',
        clockOut: 'Clock Out',
        startedAt: 'Started at',
        todayTasks: "Today's Tasks",
        announcements: 'Announcements',
        recentAnnouncements: 'Recent Announcements',
        noTasks: 'No tasks for today',
        noAnnouncements: 'No announcements',
        tasksDone: 'Tasks Done',
        shiftStatus: 'Shift Status',
        yourSchedule: 'Your Schedule',
        scheduledToday: 'Scheduled Today',
        noSchedule: 'No schedule today',
        budgetTracker: 'Budget Tracker',
        noBudget: 'No budget set.',
        setBudget: 'Set it in Payroll Analytics',
        overtimeAlerts: 'Overtime Alerts',
        longShift: 'long shift',
        nearOvertime: 'near overtime',
        viewTimesheets: 'View Timesheets',
        noOvertime: 'No overtime alerts',
        liveActivity: 'Live Activity',
        evaInsight: 'EVA INSIGHT',
        viewAll: 'View All',
        today: 'Today',
        thisWeek: 'This Week'
      },
      
      // Schedule
      schedule: {
        title: 'Schedule',
        weeklySchedule: 'Weekly shift schedule',
        addShift: 'Add Shift',
        editShift: 'Edit Shift',
        deleteShift: 'Delete Shift',
        noShifts: 'No shifts',
        scheduled: 'scheduled',
        draft: 'Draft',
        published: 'Published',
        availability: 'Availability',
        location: 'Location',
        employee: 'Employee',
        role: 'Role',
        date: 'Date',
        startTime: 'Start Time',
        endTime: 'End Time',
        breakDuration: 'Break Duration',
        notes: 'Notes',
        selectEmployee: 'Select employee',
        selectRole: 'Select role',
        selectLocation: 'Select location',
        allLocations: 'All Locations',
        minutes: 'minutes',
        hours: 'hours',
        totalHours: 'Total Hours',
        publishSchedule: 'Publish Schedule',
        unpublishSchedule: 'Unpublish Schedule'
      },
      
      // Staff
      staff: {
        title: 'Staff Management',
        subtitle: 'Manage your team members and their location assignments',
        addStaff: 'Add Staff',
        editStaff: 'Edit Staff',
        deleteStaff: 'Delete Staff',
        totalStaff: 'Total Staff',
        activeStaff: 'Active Staff',
        managers: 'Managers',
        locations: 'Locations',
        allMembers: 'All Staff Members',
        noStaff: 'No staff members yet',
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        role: 'Role',
        status: 'Status',
        active: 'Active',
        inactive: 'Inactive',
        primaryLocation: 'Primary Location',
        assignedLocations: 'Assigned Locations',
        actions: 'Actions',
        searchStaff: 'Search staff',
        filterByRole: 'Filter by role',
        filterByLocation: 'Filter by location',
        allRoles: 'All Roles',
        clockInPin: 'Clock-In PIN',
        copyPin: 'Copy PIN',
        hourlyRate: 'Hourly Rate'
      },
      
      // Time Clock
      timeclock: {
        title: 'Time Clock',
        clockIn: 'Clock In',
        clockOut: 'Clock Out',
        startBreak: 'Start Break',
        endBreak: 'End Break',
        currentShift: 'Current Shift',
        noActiveShift: 'No active shift',
        shiftStarted: 'Shift started',
        shiftDuration: 'Shift duration',
        breakTime: 'Break time',
        totalHours: 'Total hours',
        onBreak: 'On Break',
        selectLocation: 'Select location',
        recentActivity: 'Recent Activity',
        todayShifts: "Today's Shifts",
        enterPin: 'Enter your PIN',
        invalidPin: 'Invalid PIN'
      },
      
      // Timesheets/Payroll
      timesheets: {
        title: 'Timesheets',
        payroll: 'Payroll',
        weekOf: 'Week of',
        totalHours: 'Total Hours',
        regularHours: 'Regular Hours',
        overtimeHours: 'Overtime Hours',
        totalCost: 'Total Cost',
        laborCost: 'Labor Cost',
        analytics: 'Analytics',
        details: 'Details',
        export: 'Export',
        exportPDF: 'Export PDF',
        exportCSV: 'Export CSV',
        approve: 'Approve',
        reject: 'Reject',
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        employee: 'Employee',
        date: 'Date',
        clockIn: 'Clock In',
        clockOut: 'Clock Out',
        breaks: 'Breaks',
        hours: 'Hours',
        status: 'Status',
        tips: 'Tips',
        notes: 'Notes',
        noTimesheets: 'No timesheets for this period',
        selectPeriod: 'Select period',
        generatePayroll: 'Generate Payroll',
        payrollSummary: 'Payroll Summary'
      },
      
      // Common UI
      common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        add: 'Add',
        create: 'Create',
        update: 'Update',
        search: 'Search',
        filter: 'Filter',
        sort: 'Sort',
        active: 'Active',
        inactive: 'Inactive',
        enabled: 'Enabled',
        disabled: 'Disabled',
        loading: 'Loading...',
        viewAll: 'View All',
        viewDetails: 'View Details',
        close: 'Close',
        confirm: 'Confirm',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        finish: 'Finish',
        submit: 'Submit',
        reset: 'Reset',
        clear: 'Clear',
        apply: 'Apply',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
        noResults: 'No results found',
        emptyState: 'Nothing to show yet',
        error: 'Error',
        success: 'Success',
        warning: 'Warning',
        info: 'Information',
        actions: 'Actions',
        status: 'Status',
        name: 'Name',
        description: 'Description',
        date: 'Date',
        time: 'Time',
        location: 'Location',
        total: 'Total',
        subtotal: 'Subtotal',
        required: 'Required',
        optional: 'Optional',
        yes: 'Yes',
        no: 'No'
      }
    }
  },
  fr: {
    translation: {
      // Landing page
      landing: {
        hero: {
          badge: 'PLATEFORME POUR LA GESTION DE PERSONNEL AVEC IA',
          title: 'Gérez votre personnel.',
          titleHighlight: 'Opérez avec l\'IA.',
          subtitle: 'Connectez ChatGPT ou Claude directement à ReStaff. Accédez en toute sécurité aux horaires, au personnel, aux analyses, aux rapports de main-d\'œuvre et aux actions via langage naturel — avec la suite complète d\'horaires, de paie et de feuilles de temps.',
          cta: 'Démarrer l\'essai gratuit',
          demo: 'Essayer la démo',
          explore: 'Découvrir les fonctions IA',
          setup: 'Guide de configuration MCP'
        },
        pricing: {
          title: 'Tarification simple et transparente',
          subtitle: 'Aucuns frais cachés. Annulez à tout moment.',
          monthly: 'Mensuel',
          yearly: 'Annuel',
          save: 'Économisez 50%+',
          trial: 'Essai gratuit de 30 jours',
          cancel: 'Annulez à tout moment',
          includes: 'Inclut l\'assistant IA pour le personnel'
        },
        nav: {
          features: 'Fonctionnalités',
          pricing: 'Tarifs',
          help: 'Aide',
          login: 'Connexion',
          quickClock: 'Pointage rapide'
        }
      },
      
      // Navigation
      nav: {
        dashboard: 'Tableau de bord',
        staff: 'Personnel',
        timeClock: 'Pointeuse',
        schedule: 'Horaires',
        shiftSwaps: 'Échanges de quarts',
        tasks: 'Tâches',
        announcements: 'Annonces',
        notifications: 'Notifications',
        documents: 'Documents',
        insights: 'Analyses exécutives',
        timesheets: 'Feuilles de temps',
        helpCenter: 'Centre d\'aide',
        logout: 'Déconnexion',
        settings: 'Paramètres'
      },
      
      // Dashboard
      dashboard: {
        welcome: 'Bon retour, {{name}}!',
        overview: 'Aperçu',
        labor: 'Main-d\'œuvre',
        requests: 'Demandes',
        currentStatus: 'Statut actuel',
        onShift: 'En service',
        offShift: 'Hors service',
        clockIn: 'Pointer l\'entrée',
        clockOut: 'Pointer la sortie',
        startedAt: 'Commencé à',
        todayTasks: 'Tâches d\'aujourd\'hui',
        announcements: 'Annonces',
        recentAnnouncements: 'Annonces récentes',
        noTasks: 'Aucune tâche pour aujourd\'hui',
        noAnnouncements: 'Aucune annonce',
        tasksDone: 'Tâches terminées',
        shiftStatus: 'Statut du quart',
        yourSchedule: 'Votre horaire',
        scheduledToday: 'Planifié aujourd\'hui',
        noSchedule: 'Aucun horaire aujourd\'hui',
        budgetTracker: 'Suivi budgétaire',
        noBudget: 'Aucun budget défini.',
        setBudget: 'Définir dans Analyses de paie',
        overtimeAlerts: 'Alertes d\'heures supplémentaires',
        longShift: 'quart long',
        nearOvertime: 'près du seuil',
        viewTimesheets: 'Voir les feuilles de temps',
        noOvertime: 'Aucune alerte',
        liveActivity: 'Activité en direct',
        evaInsight: 'APERÇU EVA',
        viewAll: 'Voir tout',
        today: 'Aujourd\'hui',
        thisWeek: 'Cette semaine'
      },
      
      // Schedule
      schedule: {
        title: 'Horaires',
        weeklySchedule: 'Horaire hebdomadaire des quarts',
        addShift: 'Ajouter un quart',
        editShift: 'Modifier le quart',
        deleteShift: 'Supprimer le quart',
        noShifts: 'Aucun quart',
        scheduled: 'planifié',
        draft: 'Brouillon',
        published: 'Publié',
        availability: 'Disponibilité',
        location: 'Emplacement',
        employee: 'Employé',
        role: 'Rôle',
        date: 'Date',
        startTime: 'Heure de début',
        endTime: 'Heure de fin',
        breakDuration: 'Durée de pause',
        notes: 'Notes',
        selectEmployee: 'Sélectionner un employé',
        selectRole: 'Sélectionner un rôle',
        selectLocation: 'Sélectionner un emplacement',
        allLocations: 'Tous les emplacements',
        minutes: 'minutes',
        hours: 'heures',
        totalHours: 'Heures totales',
        publishSchedule: 'Publier l\'horaire',
        unpublishSchedule: 'Dépublier l\'horaire'
      },
      
      // Staff
      staff: {
        title: 'Gestion du personnel',
        subtitle: 'Gérez les membres de votre équipe et leurs affectations',
        addStaff: 'Ajouter un employé',
        editStaff: 'Modifier l\'employé',
        deleteStaff: 'Supprimer l\'employé',
        totalStaff: 'Personnel total',
        activeStaff: 'Personnel actif',
        managers: 'Gestionnaires',
        locations: 'Emplacements',
        allMembers: 'Tous les membres',
        noStaff: 'Aucun membre du personnel',
        name: 'Nom',
        email: 'Courriel',
        phone: 'Téléphone',
        role: 'Rôle',
        status: 'Statut',
        active: 'Actif',
        inactive: 'Inactif',
        primaryLocation: 'Emplacement principal',
        assignedLocations: 'Emplacements assignés',
        actions: 'Actions',
        searchStaff: 'Rechercher un employé',
        filterByRole: 'Filtrer par rôle',
        filterByLocation: 'Filtrer par emplacement',
        allRoles: 'Tous les rôles',
        clockInPin: 'NIP de pointage',
        copyPin: 'Copier le NIP',
        hourlyRate: 'Taux horaire'
      },
      
      // Time Clock
      timeclock: {
        title: 'Pointeuse',
        clockIn: 'Pointer l\'entrée',
        clockOut: 'Pointer la sortie',
        startBreak: 'Commencer la pause',
        endBreak: 'Terminer la pause',
        currentShift: 'Quart actuel',
        noActiveShift: 'Aucun quart actif',
        shiftStarted: 'Quart commencé',
        shiftDuration: 'Durée du quart',
        breakTime: 'Temps de pause',
        totalHours: 'Heures totales',
        onBreak: 'En pause',
        selectLocation: 'Sélectionner un emplacement',
        recentActivity: 'Activité récente',
        todayShifts: 'Quarts d\'aujourd\'hui',
        enterPin: 'Entrez votre NIP',
        invalidPin: 'NIP invalide'
      },
      
      // Timesheets/Payroll
      timesheets: {
        title: 'Feuilles de temps',
        payroll: 'Paie',
        weekOf: 'Semaine du',
        totalHours: 'Heures totales',
        regularHours: 'Heures régulières',
        overtimeHours: 'Heures supplémentaires',
        totalCost: 'Coût total',
        laborCost: 'Coût de main-d\'œuvre',
        analytics: 'Analyses',
        details: 'Détails',
        export: 'Exporter',
        exportPDF: 'Exporter en PDF',
        exportCSV: 'Exporter en CSV',
        approve: 'Approuver',
        reject: 'Rejeter',
        pending: 'En attente',
        approved: 'Approuvé',
        rejected: 'Rejeté',
        employee: 'Employé',
        date: 'Date',
        clockIn: 'Entrée',
        clockOut: 'Sortie',
        breaks: 'Pauses',
        hours: 'Heures',
        status: 'Statut',
        tips: 'Pourboires',
        notes: 'Notes',
        noTimesheets: 'Aucune feuille de temps pour cette période',
        selectPeriod: 'Sélectionner une période',
        generatePayroll: 'Générer la paie',
        payrollSummary: 'Résumé de paie'
      },
      
      // Common UI
      common: {
        save: 'Enregistrer',
        cancel: 'Annuler',
        delete: 'Supprimer',
        edit: 'Modifier',
        add: 'Ajouter',
        create: 'Créer',
        update: 'Mettre à jour',
        search: 'Rechercher',
        filter: 'Filtrer',
        sort: 'Trier',
        active: 'Actif',
        inactive: 'Inactif',
        enabled: 'Activé',
        disabled: 'Désactivé',
        loading: 'Chargement...',
        viewAll: 'Voir tout',
        viewDetails: 'Voir les détails',
        close: 'Fermer',
        confirm: 'Confirmer',
        back: 'Retour',
        next: 'Suivant',
        previous: 'Précédent',
        finish: 'Terminer',
        submit: 'Soumettre',
        reset: 'Réinitialiser',
        clear: 'Effacer',
        apply: 'Appliquer',
        selectAll: 'Tout sélectionner',
        deselectAll: 'Tout désélectionner',
        noResults: 'Aucun résultat',
        emptyState: 'Rien à afficher',
        error: 'Erreur',
        success: 'Succès',
        warning: 'Avertissement',
        info: 'Information',
        actions: 'Actions',
        status: 'Statut',
        name: 'Nom',
        description: 'Description',
        date: 'Date',
        time: 'Heure',
        location: 'Emplacement',
        total: 'Total',
        subtotal: 'Sous-total',
        required: 'Requis',
        optional: 'Optionnel',
        yes: 'Oui',
        no: 'Non'
      }
    }
  }
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: localStorage.getItem('language') || 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    }
  });

export default i18n;
